import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from 'react-hot-toast';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Players from './pages/Players';
import Coaches from './pages/Coaches';
import Sessions from './pages/Sessions';
import SessionDetails from './pages/SessionDetails';
import SessionSummary from './pages/SessionSummary';
import BulkSessionCreation from './pages/BulkSessionCreation';
import AttendanceMarking from './pages/AttendanceMarking';
import Settings from './pages/Settings';
import SetPassword from './pages/SetPassword';
import Layout from './components/Layout';
import MobileInstallBanner from './components/MobileInstallBanner';
import SessionPlanningReport from './components/SessionPlanningReport';
import AttendanceReportModal from './components/AttendanceReportModal';
import SupabaseConnectionBanner from './components/SupabaseConnectionBanner';
import './App.css';

// Protected route component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

// Admin route component
const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();
  
  if (!user || (user.role !== 'admin' && user.role !== 'superadmin')) {
    return <Navigate to="/" replace />;
  }
  
  return <>{children}</>;
};

function App() {
  const [showPlanningReport, setShowPlanningReport] = useState(false);
  const [showAttendanceReport, setShowAttendanceReport] = useState(false);

  return (
    <AuthProvider>
      <Router basename="/">
        <div className="min-h-screen bg-gray-50">
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/set-password" element={<SetPassword />} />
            <Route path="/" element={
              <ProtectedRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/players" element={
              <ProtectedRoute>
                <Layout>
                  <Players />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/coaches" element={
              <ProtectedRoute>
                <Layout>
                  <Coaches />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/sessions" element={
              <ProtectedRoute>
                <Layout>
                  <Sessions />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/sessions/new" element={
              <ProtectedRoute>
                <Layout>
                  <SessionDetails />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/sessions/bulk-create" element={
              <ProtectedRoute>
                <Layout>
                  <BulkSessionCreation />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/sessions/:id" element={
              <ProtectedRoute>
                <Layout>
                  <SessionDetails />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/sessions/:id/attendance" element={
              <ProtectedRoute>
                <Layout>
                  <AttendanceMarking />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/sessions/:id/summary" element={
              <ProtectedRoute>
                <Layout>
                  <SessionSummary />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <AdminRoute>
                <Layout>
                  <Settings />
                </Layout>
              </AdminRoute>
            } />
          </Routes>
          <MobileInstallBanner />
          <SupabaseConnectionBanner />
          {showPlanningReport && <SessionPlanningReport onClose={() => setShowPlanningReport(false)} />}
          {showAttendanceReport && <AttendanceReportModal onClose={() => setShowAttendanceReport(false)} />}
          <Toaster 
            position="top-right"
            toastOptions={{
              duration: 3000,
              style: {
                background: '#002855',
                color: '#fff',
                padding: '16px',
                borderRadius: '8px',
              },
              success: {
                iconTheme: {
                  primary: '#fff',
                  secondary: '#002855',
                },
              },
              error: {
                style: {
                  background: '#dc2626',
                },
                iconTheme: {
                  primary: '#fff',
                  secondary: '#dc2626',
                },
              },
            }}
          />
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;